import React from "react";
import { v4 as uuid } from "uuid";
import { GeneralWebAnalyticsData, VideoWebAnalyticsData } from "../types";
import { analyticsService } from "../services";

interface WebAnalyticsContextType {
    generalWebAnalyticsData: GeneralWebAnalyticsData;
    videoWebAnalyticsData: VideoWebAnalyticsData[];
    updateGeneralWebAnalyticsData: (newGeneral: Partial<GeneralWebAnalyticsData>) => void;
    updateVideoWebAnalyticsData: (newVideo: VideoWebAnalyticsData) => void;
    setSessionToken: (sessionToken: string) => void;
}

const initialStateGeneral: GeneralWebAnalyticsData =
{
    mediaId: "",
    anonId: "",
    sequence: 0,
    pageUrl: "",
    timeSpent: 0,
    httpReferer: "",
    browserVersion: "",
    osVersion: "",
    windowWidth: 0,
    geoInfo: "",
    errorLog: "",
}


export const WebAnalyticsContext = React.createContext<WebAnalyticsContextType>(
    {
        generalWebAnalyticsData: initialStateGeneral,
        videoWebAnalyticsData: [],
        updateGeneralWebAnalyticsData: () => { },
        updateVideoWebAnalyticsData: () => { },
        setSessionToken: () => { },
    }
);

export const WebAnalyticsProvider: React.FC<{ enableAnalytics: boolean, clientId: string | null, children: React.ReactNode }> = ({
    enableAnalytics, clientId, children
}) => {
    const [generalWebAnalyticsData, setGeneralWebAnalyticsData] =
        React.useState<GeneralWebAnalyticsData>(initialStateGeneral);
    const [videoWebAnalyticsData, setVideoWebAnalyticsData] =
        React.useState<VideoWebAnalyticsData[]>([]);
    const [enterTimestamp, setEnterTimestamp] = React.useState<number>(0);
    const generalWebAnalyticsDataRef = React.useRef(generalWebAnalyticsData);
    const videoWebAnalyticsDataRef = React.useRef(videoWebAnalyticsData);
    const enterTimestampRef = React.useRef(enterTimestamp);
    const [sessionToken, setSessionToken] = React.useState<string>("");

    // Update refs whenever state changes
    React.useEffect(() => {
        generalWebAnalyticsDataRef.current = generalWebAnalyticsData;
        videoWebAnalyticsDataRef.current = videoWebAnalyticsData;
        enterTimestampRef.current = enterTimestamp;
    }, [generalWebAnalyticsData, videoWebAnalyticsData, enterTimestamp]);


    const getMacOSVersion = (userAgent: string): string => {
        let version = "Unknown Version";

        const macosVersionMap: { [key: string]: string } = {
            "10_15": "Catalina",
            "10_14": "Mojave",
            "10_13": "High Sierra",
            "10_12": "Sierra",
            "10_11": "El Capitan",
        };

        const match = userAgent.match(/Mac OS X (10_[0-9_]+)/);

        if (match && match[1]) {
            version = macosVersionMap[match[1]] || `Unknown (${match[1]})`;
        }

        return version;
    };

    const getOSAndVersion = () => {
        const userAgent = window.navigator.userAgent;
        const platform = window.navigator.platform;
        let os = "Unknown OS";
        let version = "Unknown Version";

        if (platform.startsWith("Win")) {
            os = "Windows";
            if (userAgent.includes("Windows NT 10.0")) version = "10";
            else if (userAgent.includes("Windows NT 6.3")) version = "8.1";
            else if (userAgent.includes("Windows NT 6.2")) version = "8";
            else if (userAgent.includes("Windows NT 6.1")) version = "7";
        } else if (platform.startsWith("Mac")) {
            os = "macOS";
            const version = getMacOSVersion(userAgent);
            return `${os} ${version}`;
        } else if (platform.startsWith("Linux")) {
            os = "Linux";
        }

        return `${os} ${version}`;
    };

    const updateVideoWebAnalyticsData = React.useCallback(
        (newVideo: VideoWebAnalyticsData) => {
            setVideoWebAnalyticsData((prev) => [...prev, { ...newVideo }]);
        },
        [setVideoWebAnalyticsData]
    );

    const updateGeneralWebAnalyticsData = React.useCallback(
        (newGeneral: Partial<GeneralWebAnalyticsData>) => {
            setGeneralWebAnalyticsData((prev) => ({ ...prev, ...newGeneral }));
        },
        [setGeneralWebAnalyticsData]
    );

    const unloadHandler = React.useCallback(async () => {
        const exitTimestamp = Date.now();
        const timeSpent = exitTimestamp - enterTimestampRef.current;
        updateGeneralWebAnalyticsData({ timeSpent });
        generalWebAnalyticsDataRef.current.timeSpent = timeSpent;
        await analyticsService.sendGeneralData(generalWebAnalyticsDataRef.current);
        for (const video of videoWebAnalyticsDataRef.current) {
            await analyticsService.sendVideoData(video);
        }
    }, [updateGeneralWebAnalyticsData]);

    const errorLogHanlder = React.useCallback((log_object: string) => {
        updateGeneralWebAnalyticsData({
            errorLog: generalWebAnalyticsData.errorLog + "\n\n\n\n" + log_object,
        },);
    }, [generalWebAnalyticsData, updateGeneralWebAnalyticsData]);

    React.useEffect(() => {
        if (!localStorage.getItem("anonId")) {
            localStorage.setItem("anonId", uuid());
        }

        const anonId = localStorage.getItem("anonId");
        const pageUrl = window.location.href;
        const browserVersion = navigator.userAgent;
        const osVersion = getOSAndVersion();
        const windowWidth = window.innerWidth;
        const geoInfo = "Earth";
        const httpReferer = document.referrer;

        updateGeneralWebAnalyticsData({
            anonId: anonId || "",
            pageUrl,
            browserVersion,
            osVersion,
            windowWidth,
            geoInfo,
            httpReferer,
        });
        setEnterTimestamp(Date.now());

        if (enableAnalytics) window.addEventListener("beforeunload", unloadHandler);
        window.onerror = function (error, url, line) {
            errorLogHanlder('ERR:' + error + ' URL:' + url + ' L:' + line);
        };
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        if (!enableAnalytics) return;
        if (videoWebAnalyticsData.length === 0) return;
        while (true) {
            const video = videoWebAnalyticsData.pop();
            if (video) analyticsService.sendVideoData(video, sessionToken, clientId);
            else break;
        }
    }, [videoWebAnalyticsData, sessionToken, enableAnalytics, clientId]);

    return (
        <WebAnalyticsContext.Provider
            value={{ generalWebAnalyticsData, videoWebAnalyticsData, updateGeneralWebAnalyticsData, updateVideoWebAnalyticsData, setSessionToken }}
        >
            {children}
        </WebAnalyticsContext.Provider>
    );
};
